/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Content 2 PD component — two stacked content cards (image + eyebrow + title + body + CTA each)
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';
import {
    FULL_IMAGE_CLASSES,
    getAlignClasses,
    getMarginBottomClass,
    getTextStyle,
    isFullImage,
} from '@/lib/page-designer/style-attributes';

// RGBARRY-CUSTOMIZATION: validate merchant-authored colors and Vimeo URLs before rendering inline values.
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const parseOptionalHex = (value: string | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : undefined;
};

interface VimeoVideo {
    id: string;
    privacyHash?: string;
}

const parseVimeoVideo = (value: string | undefined): VimeoVideo | null => {
    const trimmed = value?.trim();
    if (!trimmed) return null;

    try {
        const url = new URL(trimmed);
        const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
        const pathSegments = url.pathname.split('/').filter(Boolean);
        const isPlayerUrl = hostname === 'player.vimeo.com';

        if (hostname !== 'vimeo.com' && !isPlayerUrl) return null;

        const id = isPlayerUrl || pathSegments[0] === 'video' ? pathSegments[1] : pathSegments[0];
        if (!id || !/^\d+$/.test(id)) return null;

        // Unlisted share links use /{id}/{hash}; player URLs use ?h={hash}. Vimeo requires
        // that hash to remain the first player query parameter or the embed cannot be resolved.
        const pathHash = isPlayerUrl || pathSegments[0] === 'video' ? pathSegments[2] : pathSegments[1];
        const privacyHash = url.searchParams.get('h') ?? pathHash;

        return {
            id,
            ...(/^[-_a-zA-Z0-9]+$/.test(privacyHash ?? '') ? { privacyHash } : {}),
        };
    } catch {
        return null;
    }
};

const getVimeoPlayerUrl = ({ id, privacyHash }: VimeoVideo): string => {
    const params = new URLSearchParams();
    if (privacyHash) params.set('h', privacyHash);
    params.set('background', '1');
    params.set('autoplay', '1');
    params.set('muted', '1');
    params.set('loop', '1');
    params.set('autopause', '0');
    params.set('playsinline', '1');
    params.set('dnt', '1');
    return `https://player.vimeo.com/video/${id}?${params.toString()}`;
};
// END RGBARRY-CUSTOMIZATION

/* v8 ignore start */
@Component('contentTwo', {
    name: 'Content 2',
    description:
        'Two stacked/side-by-side content cards with configurable colors. Each card supports an image or Vimeo video, eyebrow, title, body copy, and a CTA.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ContentTwoMetadata {
    // RGBARRY-CUSTOMIZATION: expose safe per-instance section and editorial text colors in Page Designer.
    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color Hex',
        description: 'Optional #RGB or #RRGGBB color. Leave empty to use the storefront background color.',
        type: 'string',
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'fontColor',
        name: 'Font Color Hex',
        description: 'Optional #RGB or #RRGGBB color for editorial text and text-link CTAs.',
        type: 'string',
    })
    fontColor?: string;
    // END RGBARRY-CUSTOMIZATION

    // Card 1
    @AttributeDefinition({ id: 'card1Eyebrow', name: 'Card 1 Eyebrow', type: 'string' })
    card1Eyebrow?: string;

    @AttributeDefinition({ id: 'card1Title', name: 'Card 1 Title', type: 'string' })
    card1Title?: string;

    @AttributeDefinition({ id: 'card1Body', name: 'Card 1 Body Copy', type: 'text' })
    card1Body?: string;

    @AttributeDefinition({ id: 'card1CtaText', name: 'Card 1 CTA Text', type: 'string' })
    card1CtaText?: string;

    @AttributeDefinition({ id: 'card1CtaLink', name: 'Card 1 CTA Link', type: 'url' })
    card1CtaLink?: string;

    @AttributeDefinition({
        id: 'card1CtaStyle',
        name: 'Card 1 CTA Style',
        description: 'Render the CTA as a filled button or an underlined text link',
        type: 'enum',
        values: ['button', 'link'],
        defaultValue: 'button',
    })
    card1CtaStyle?: string;

    @AttributeDefinition({ id: 'card1Image', name: 'Card 1 Image', type: 'image' })
    card1Image?: Image;

    // RGBARRY-CUSTOMIZATION: allow Card 1 Vimeo media to override its authored image.
    @AttributeDefinition({
        id: 'card1VimeoUrl',
        name: 'Card 1 Vimeo URL',
        description: 'Optional Vimeo URL. When valid, the video is shown instead of Card 1 Image.',
        type: 'string',
    })
    card1VimeoUrl?: string;
    // END RGBARRY-CUSTOMIZATION

    @AttributeDefinition({ id: 'card1ImageAlt', name: 'Card 1 Image Alt Text', type: 'string' })
    card1ImageAlt?: string;

    // Card 2
    @AttributeDefinition({ id: 'card2Eyebrow', name: 'Card 2 Eyebrow', type: 'string' })
    card2Eyebrow?: string;

    @AttributeDefinition({ id: 'card2Title', name: 'Card 2 Title', type: 'string' })
    card2Title?: string;

    @AttributeDefinition({ id: 'card2Body', name: 'Card 2 Body Copy', type: 'text' })
    card2Body?: string;

    @AttributeDefinition({ id: 'card2CtaText', name: 'Card 2 CTA Text', type: 'string' })
    card2CtaText?: string;

    @AttributeDefinition({ id: 'card2CtaLink', name: 'Card 2 CTA Link', type: 'url' })
    card2CtaLink?: string;

    @AttributeDefinition({
        id: 'card2CtaStyle',
        name: 'Card 2 CTA Style',
        description: 'Render the CTA as a filled button or an underlined text link',
        type: 'enum',
        values: ['button', 'link'],
        defaultValue: 'button',
    })
    card2CtaStyle?: string;

    @AttributeDefinition({ id: 'card2Image', name: 'Card 2 Image', type: 'image' })
    card2Image?: Image;

    // RGBARRY-CUSTOMIZATION: allow Card 2 Vimeo media to override its authored image.
    @AttributeDefinition({
        id: 'card2VimeoUrl',
        name: 'Card 2 Vimeo URL',
        description: 'Optional Vimeo URL. When valid, the video is shown instead of Card 2 Image.',
        type: 'string',
    })
    card2VimeoUrl?: string;
    // END RGBARRY-CUSTOMIZATION

    @AttributeDefinition({ id: 'card2ImageAlt', name: 'Card 2 Image Alt Text', type: 'string' })
    card2ImageAlt?: string;

    // Style controls (shared by both cards; text color comes from Font Color Hex above)
    @AttributeDefinition({
        id: 'headingFontStyle',
        name: 'Style – Heading Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    headingFontStyle?: string;

    @AttributeDefinition({
        id: 'headingFontSize',
        name: 'Style – Heading Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    headingFontSize?: string;

    @AttributeDefinition({
        id: 'bodyFontStyle',
        name: 'Style – Body Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    bodyFontStyle?: string;

    @AttributeDefinition({
        id: 'bodyFontSize',
        name: 'Style – Body Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    bodyFontSize?: string;

    @AttributeDefinition({
        id: 'textAlign',
        name: 'Style – Text Alignment',
        type: 'enum',
        values: ['Default', 'Left', 'Center', 'Right'],
        defaultValue: 'Default',
    })
    textAlign?: string;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Style – Margin Bottom',
        type: 'enum',
        values: ['0', '2', '4', '6', '8', '12', '16'],
        defaultValue: '0',
        description: 'Space below the component (Tailwind spacing scale; 4 = 16px).',
    })
    marginBottom?: string;

    @AttributeDefinition({
        id: 'imageDisplay',
        name: 'Style – Image Display',
        type: 'enum',
        values: ['Crop to fit', 'Show full image'],
        defaultValue: 'Crop to fit',
        description: 'Show full image keeps the whole image at its natural height (no top/bottom crop).',
    })
    imageDisplay?: string;
}
/* v8 ignore stop */

interface CardProps {
    eyebrow?: string;
    title?: string;
    body?: string;
    ctaText?: string;
    ctaLink?: string;
    /** 'button' (default, filled) or 'link' (underlined text link) */
    ctaStyle?: string;
    image?: Image;
    // RGBARRY-CUSTOMIZATION: thread optional Vimeo and validated text color values into each card.
    vimeoUrl?: string;
    fontColor?: string;
    // END RGBARRY-CUSTOMIZATION
    imageAlt?: string;
    inView: boolean;
    delay: number;
    styles: CardStyles;
}

interface CardStyles {
    heading: ReturnType<typeof getTextStyle>;
    body: ReturnType<typeof getTextStyle>;
    align: ReturnType<typeof getAlignClasses>;
    fullImage: boolean;
}

function ContentCard({
    eyebrow,
    title,
    body,
    ctaText,
    ctaLink,
    ctaStyle,
    image,
    vimeoUrl,
    fontColor,
    imageAlt,
    inView,
    delay,
    styles,
}: CardProps) {
    // RGBARRY-CUSTOMIZATION: valid Vimeo media replaces the image while retaining the card's CTA overlay.
    const vimeoVideo = parseVimeoVideo(vimeoUrl);
    const showMedia = Boolean(vimeoVideo || image?.url);
    const textStyle = fontColor ? { color: fontColor } : undefined;
    // END RGBARRY-CUSTOMIZATION
    const { heading, body: bodyText, align } = styles;
    // Video needs a sized box, so full-image mode only applies to images.
    const fullImage = styles.fullImage && !vimeoVideo;

    return (
        <div
            className={cn('flex flex-col df-reveal', inView && 'df-reveal--show')}
            style={{ transitionDelay: inView ? `${delay}ms` : '0ms' }}>
            {showMedia && (
                <div className={cn('relative overflow-hidden rounded-ui bg-muted mb-6', !fullImage && 'aspect-square')}>
                    {/* RGBARRY-CUSTOMIZATION: render a muted looping Vimeo background, falling back to the image. */}
                    {vimeoVideo ? (
                        <iframe
                            src={getVimeoPlayerUrl(vimeoVideo)}
                            title={imageAlt || 'Card video'}
                            allow="autoplay; fullscreen; picture-in-picture"
                            loading="lazy"
                            tabIndex={-1}
                            className="pointer-events-none absolute inset-0 h-full w-full"
                            frameBorder={0}
                        />
                    ) : (
                        image?.url && (
                            <DynamicImage
                                src={image.url}
                                alt={imageAlt ?? ''}
                                widths={{ base: 768, lg: 720 }}
                                className={fullImage ? 'block w-full' : 'absolute inset-0 w-full h-full'}
                                imageProps={{
                                    className: cn(
                                        'transition-transform duration-700 hover:scale-105',
                                        fullImage ? FULL_IMAGE_CLASSES : 'w-full h-full object-cover'
                                    ),
                                }}
                            />
                        )
                    )}
                    {/* END RGBARRY-CUSTOMIZATION */}
                    {/* RGBARRY-CUSTOMIZATION: link card media whenever a destination is authored,
                        even when the optional visible CTA text is intentionally left empty. */}
                    {ctaLink && <StretchedLink to={ctaLink} />}
                </div>
            )}
            <div className={cn('flex flex-col gap-4 px-1', align?.text, align?.items)}>
                {eyebrow && (
                    <p style={textStyle} className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                        {eyebrow}
                    </p>
                )}
                {title && (
                    <h3
                        style={{ fontFamily: 'var(--font-display)', ...textStyle, ...heading.style }}
                        className={cn(
                            'text-2xl lg:text-3xl font-light leading-snug text-foreground',
                            heading.className
                        )}>
                        {title}
                    </h3>
                )}
                {body && (
                    <p
                        style={{ ...textStyle, ...bodyText.style }}
                        className={cn('text-sm text-muted-foreground leading-relaxed', bodyText.className)}>
                        {body}
                    </p>
                )}
                {ctaText &&
                    ctaLink &&
                    (ctaStyle === 'link' ? (
                        <Link
                            to={ctaLink}
                            style={textStyle}
                            className={cn(
                                align ? 'self-auto' : 'self-start',
                                'text-sm font-medium uppercase tracking-[0.12em] text-foreground underline underline-offset-[6px] decoration-1 hover:opacity-70 transition-opacity'
                            )}>
                            {ctaText}
                        </Link>
                    ) : (
                        <Button
                            asChild
                            className={cn(
                                'df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2',
                                align ? 'self-auto' : 'self-start'
                            )}>
                            <Link to={ctaLink}>{ctaText}</Link>
                        </Button>
                    ))}
            </div>
        </div>
    );
}

export default function ContentTwo({
    backgroundColor,
    fontColor,
    card1Eyebrow,
    card1Title,
    card1Body,
    card1CtaText,
    card1CtaLink,
    card1CtaStyle,
    card1Image,
    card1VimeoUrl,
    card1ImageAlt,
    card2Eyebrow,
    card2Title,
    card2Body,
    card2CtaText,
    card2CtaLink,
    card2CtaStyle,
    card2Image,
    card2VimeoUrl,
    card2ImageAlt,
    headingFontStyle,
    headingFontSize,
    bodyFontStyle,
    bodyFontSize,
    textAlign,
    marginBottom,
    imageDisplay,
}: {
    // RGBARRY-CUSTOMIZATION: Page Designer section color controls.
    backgroundColor?: string;
    fontColor?: string;
    // END RGBARRY-CUSTOMIZATION
    card1Eyebrow?: string;
    card1Title?: string;
    card1Body?: string;
    card1CtaText?: string;
    card1CtaLink?: string;
    card1CtaStyle?: string;
    card1Image?: Image;
    // RGBARRY-CUSTOMIZATION: Page Designer Card 1 Vimeo override.
    card1VimeoUrl?: string;
    // END RGBARRY-CUSTOMIZATION
    card1ImageAlt?: string;
    card2Eyebrow?: string;
    card2Title?: string;
    card2Body?: string;
    card2CtaText?: string;
    card2CtaLink?: string;
    card2CtaStyle?: string;
    card2Image?: Image;
    // RGBARRY-CUSTOMIZATION: Page Designer Card 2 Vimeo override.
    card2VimeoUrl?: string;
    // END RGBARRY-CUSTOMIZATION
    card2ImageAlt?: string;
    headingFontStyle?: string;
    headingFontSize?: string;
    bodyFontStyle?: string;
    bodyFontSize?: string;
    textAlign?: string;
    marginBottom?: string;
    imageDisplay?: string;
}) {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-80px' });
    // RGBARRY-CUSTOMIZATION: ignore invalid merchant color strings instead of injecting arbitrary CSS.
    const authoredBackgroundColor = parseOptionalHex(backgroundColor);
    const authoredFontColor = parseOptionalHex(fontColor);
    // END RGBARRY-CUSTOMIZATION
    const styles: CardStyles = {
        heading: getTextStyle({ fontStyle: headingFontStyle, fontSize: headingFontSize }, 'heading'),
        body: getTextStyle({ fontStyle: bodyFontStyle, fontSize: bodyFontSize }, 'body'),
        align: getAlignClasses(textAlign),
        fullImage: isFullImage(imageDisplay),
    };

    // RGBARRY-CUSTOMIZATION: apply the validated per-instance section background color.
    return (
        <section
            ref={ref}
            className={cn('bg-background py-10 lg:py-14', getMarginBottomClass(marginBottom))}
            style={authoredBackgroundColor ? { backgroundColor: authoredBackgroundColor } : undefined}>
            <div className="section-container px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
                    <ContentCard
                        eyebrow={card1Eyebrow}
                        title={card1Title}
                        body={card1Body}
                        ctaText={card1CtaText}
                        ctaLink={card1CtaLink}
                        ctaStyle={card1CtaStyle}
                        image={card1Image}
                        vimeoUrl={card1VimeoUrl}
                        fontColor={authoredFontColor}
                        imageAlt={card1ImageAlt}
                        inView={inView}
                        styles={styles}
                        delay={0}
                    />
                    <ContentCard
                        eyebrow={card2Eyebrow}
                        title={card2Title}
                        body={card2Body}
                        ctaText={card2CtaText}
                        ctaLink={card2CtaLink}
                        ctaStyle={card2CtaStyle}
                        image={card2Image}
                        vimeoUrl={card2VimeoUrl}
                        fontColor={authoredFontColor}
                        imageAlt={card2ImageAlt}
                        inView={inView}
                        styles={styles}
                        delay={120}
                    />
                </div>
            </div>
        </section>
    );
}
